<template>
  <div>
    <v-text-field
      v-model="localBackendId"
      :label="t('xp-messenger.partner.backendId')"
      :hint="t('xp-messenger.partner.ecnHint')"
      :error-messages="errors?.backendId"
      persistent-hint
      variant="outlined"
      class="mb-4"
      @input="clearError('backendId')"
    />
    
    <v-text-field
      v-model="localDisplayName"
      :label="t('xp-messenger.partner.displayName')"
      :hint="t('xp-messenger.partner.nameHint')"
      :error-messages="errors?.displayName"
      persistent-hint
      variant="outlined"
      class="mb-4"
      @input="clearError('displayName')"
    />

    <v-text-field
      v-if="showPassword"
      v-model="localPassword"
      :label="t('xp-messenger.partner.password')"
      :hint="t('xp-messenger.partner.passwordHint')"
      :error-messages="errors?.password"
      :type="showPasswordText ? 'text' : 'password'"
      persistent-hint
      variant="outlined"
      class="mb-4"
      @input="clearError('password')"
    >
      <template v-slot:append>
        <v-icon
          :icon="showPasswordText ? 'mdi-eye-off' : 'mdi-eye'"
          @click="showPasswordText = !showPasswordText"
        />
      </template>
    </v-text-field>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

/**
 * Props for the PartnerFields component
 * @typedef {Object} Props
 * @property {Object} modelValue - The v-model value containing backendId and displayName
 * @property {Object} errors - Object containing field-specific error messages
 * @property {string} operationType - Type of operation (e.g., 'create', 'certificate')
 * @property {boolean} showPassword - Whether to show the password field
 */
const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
    validator: (value) => {
      return 'backendId' in value && 'displayName' in value;
    }
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  operationType: {
    type: String,
    required: true
  },
  showPassword: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'clearError']);

/**
 * State for password visibility toggle
 */
const showPasswordText = ref(false);

/**
 * Computed property for backendId field with two-way binding
 */
const localBackendId = computed({
  get: () => props.modelValue.backendId,
  set: (value) => emit('update:modelValue', { ...props.modelValue, backendId: value })
});

/**
 * Computed property for displayName field with two-way binding
 */
const localDisplayName = computed({
  get: () => props.modelValue.displayName,
  set: (value) => emit('update:modelValue', { ...props.modelValue, displayName: value })
});

/**
 * Computed property for password field with two-way binding
 */
const localPassword = computed({
  get: () => props.modelValue.password || '',
  set: (value) => emit('update:modelValue', { ...props.modelValue, password: value })
});

/**
 * Clears error for a specific field
 * @param {string} field - The field name to clear error for
 */
const clearError = (field) => {
  emit('clearError', field);
};
</script> 